import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import { Box, DialogContent } from '@mui/material'
import { FormikProvider, useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import React, { useEffect, useState } from 'react'

import ContainedButton from 'shared/components/Buttons/ContainedButton'
import OutlinedButton from 'shared/components/Buttons/OutlinedButton'
import { CustomerContactType } from 'shared/store/useCustomers/types'
import InputField from 'shared/components/InputField'
import validatePhoneNumber from 'shared/utils/helpers/validatePhoneNumber'
import MuiPhone from 'shared/components/PhoneInput'

interface Props {
   onSubmit: (values: CustomerContactType) => void
   onClose: () => void
   contactDetails?: CustomerContactType
   isSubmitting: boolean
}

export default function DetailsModal({ onSubmit, isSubmitting, onClose, contactDetails }: Props) {
   const { t } = useTranslation(['TRANSLATION', 'CUSTOMERS'])
   const [phoneNumberError, setPhoneNumberError] = useState<boolean>(false)

   function renderTitle() {
      if (contactDetails) {
         return t('CUSTOMERS:modal.contactDetails.editTitle', {
            contactName: `${contactDetails.name} ${contactDetails.surname}`,
         })
      }

      return t('CUSTOMERS:modal.contactDetails.title')
   }

   const formik = useFormik<CustomerContactType>({
      initialValues: {
         name: '',
         surname: '',
         phoneNumber: '',
         email: '',
         comment: '',
         uuid: '',
      },
      onSubmit: (values) => {
         onSubmit({
            ...values,
            phoneNumber: values.phoneNumber === '+48' || values.phoneNumber === '+43' ? '' : values.phoneNumber,
         })
      },
   })

   const { handleSubmit, setFieldValue, values, setValues } = formik

   function handlePhoneNumber(value: string, country: string) {
      setFieldValue(`phoneNumber`, value)
      const result = validatePhoneNumber(value, country)
      if (result === false) {
         setPhoneNumberError(true)
      } else {
         setPhoneNumberError(false)
      }
   }

   useEffect(() => {
      if (contactDetails) {
         setValues({ ...contactDetails })
      }
   }, [contactDetails])

   return (
      <>
         <DialogTitle>{renderTitle()}</DialogTitle>
         <DialogContent>
            <FormikProvider value={formik}>
               <Box sx={{ py: '8px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <Box sx={{ display: 'flex', gap: '8px', flexDirection: { lg: 'row', md: 'row', xs: 'column' } }}>
                     <InputField fullWidth name="name" label={t('TRANSLATION:formFields.name')} />
                     <InputField fullWidth name="surname" label={t('TRANSLATION:formFields.surname')} />
                     <InputField fullWidth name="email" label="E-mail" />
                     <MuiPhone
                        notRequired
                        label={t('TRANSLATION:formFields.clientPhoneNumber')}
                        variant="outlined"
                        value={values.phoneNumber!}
                        onChange={(value, country) => handlePhoneNumber(value, country)}
                     />{' '}
                  </Box>
                  <InputField
                     fullWidth
                     name="comment"
                     label={t('TRANSLATION:columnNames.description')}
                     multiline
                     rows={2}
                  />
               </Box>
            </FormikProvider>
         </DialogContent>
         <DialogActions>
            <OutlinedButton variant="outlined" disabled={isSubmitting} onClick={onClose}>
               {t('TRANSLATION:cancel')}
            </OutlinedButton>
            <ContainedButton variant="contained" disabled={isSubmitting} onClick={() => handleSubmit()}>
               {t('TRANSLATION:save')}
            </ContainedButton>
         </DialogActions>
      </>
   )
}
